import { useCallback, useContext, useEffect, useState } from "react";
import { EntityDetailProps, EntityDetailTableProps } from "../../common/entity/EntityInterfaces";
import Detail from "../../common/panels/Detail/Detail";
import { DetailsPageTopControlsView } from "../../common/panels/misc/DetailsPageTopControlsView/DetailsPageTopControlsView";
import { camelCaseToWords, toUppercase } from "../../common/helperfunctions/stringFunctions";
import { LucideIcon } from "../../common/icon/LucideIcon";
import { useHistory } from "react-router-dom";
import { SessionContext } from "../../common/contexts/SessionContext";
import { getEditLink, getIndexRoute } from "../../main/Routing";
import { MoreDropdown } from "../../common/buttons/MoreDropdown/MoreDropdown";
import { DatasetsTile } from "../../common/sidebar/tiles/DatasetsTile";
import { InventoriesTile } from "../../common/sidebar/tiles/InventoriesTile";
import { PersonsTile } from "../../common/sidebar/tiles/PersonsTile";
import { ProjectsTile } from "../../common/sidebar/tiles/ProjectsTile";
import { SamplesTile } from "../../common/sidebar/tiles/SamplesTile";
import { Table } from "../../common/panels/Detail/DetailTable";
import { NotAvailable, NotSet } from "../../common/misc/UIconstants";
import {
  CustomTypeEntityTypeOptionToIcon,
  CustomTypeHierarchyViewer,
  GridLayoutElementWrapper,
  RenderCustomTypeDefaultSections,
  RenderCustomTypeHierarchy,
} from "./CustomTypeRenderUtils";
import { ToggleIndicator } from "../../common/badges/ToggleIndicator/ToggleIndicator";
import { useResizeDetector } from "react-resize-detector";
import GridLayout from "react-grid-layout";
import { v4 as uuidv4 } from "uuid";
import { useDynamicHeight } from "./hooks/useDynamicHeight";
import { CustomField, customFieldConstants, CustomFieldFieldLabels } from "../../api/CustomFields/CustomFields";
import { CustomFieldsDetailTable } from "../CustomFields/CustomFieldsDetailView";
import { AbstractedCustomFieldDataTypeIcon } from "../CustomFields/CustomFieldRenderUtils";
import { generateAbstractedFromDataType } from "../CustomFields/CustomFieldUtils";
import { LinkEntity } from "../../common/misc/LinkEntity/LinkEntity";
import { IdTypes, IEntityMinimalModel } from "../../api/GenericTypes";
import { hierarchyConstants } from "../../api/Inventories";
import { LinkButton } from "../../common/buttons/LinkButton/LinkButton";
import { Alert } from "../../common/overlays/Alert/Alert";
import { CustomTypeDataTypeUtils } from "../../api/CustomTypes/CustomTypesBase";
import { CustomTypeSection } from "../../api/CustomTypes/CustomTypes";
import { EntitySidebarTileAutosorter } from "../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTileAutosorter";
import { EntityDeleteButton } from "../../Entities/GenericComponents/common/GenericEntityDetailPage";
import styles from "./CustomTypeBuilder.module.css";
import { EntityEditButton } from "../../common/entity/entityComponents/EntityEditButton";
import { EntityCloneButton } from "../../common/entity/entityComponents/EntityCloneButton";
import { customTypeTemplateConstants } from "../../api/CustomTypes/CustomTypeTemplates";
import { TableArrayRenderer } from "../../common/misc/EntityRenders/EntityRenderer";
import { CustomTypePreviewButton } from "./generics/CustomTypePreviewRenderer";

interface RenderCustomTypeSectionCustomFieldProps<Field extends Partial<CustomField>> {
  elementId: string;
  customField: Field;
  nodeHeightCallback: (uuid: string, height: number) => void;
  removeCustomField?: (elementId: string) => void;
  canEdit: boolean;
}
export const RenderCustomTypeSectionCustomField = <Field extends Partial<CustomField>>({
  elementId,
  customField,
  nodeHeightCallback,
  removeCustomField,
  canEdit,
}: RenderCustomTypeSectionCustomFieldProps<Field>) => {
  const history = useHistory();
  const { route } = useContext(SessionContext);
  const { ref } = useDynamicHeight({
    uuid: elementId,
    nodeHeightCallback: nodeHeightCallback,
    gridRowHeight: 1,
  });
  const abstracted = generateAbstractedFromDataType(customField.dataType);
  return (
    <GridLayoutElementWrapper
      isFolded
      head={
        <div className="flex row-nowrap align-center gap-5" style={{ width: "100%", height: "100%" }}>
          {abstracted?.abstractedDataType ? (
            <AbstractedCustomFieldDataTypeIcon
              dataType={abstracted.abstractedDataType}
              style={{ width: "24px", height: "24px" }}
            />
          ) : (
            <LucideIcon name={customFieldConstants.icon} style={{ width: "24px", height: "24px" }} />
          )}
          <h4 style={{ margin: 0 }}>{customField.name}</h4>
          <div className="flex row-nowrap align-center gap-5" style={{ height: "100%", marginLeft: "auto" }}>
            <div className={"container_label"} onMouseDown={(e) => e.stopPropagation()}>
              <div className={"container_label_id"}>
                <span style={{ whiteSpace: "nowrap" }}>
                  <LinkEntity
                    entityConstants={customFieldConstants}
                    property={customField as IEntityMinimalModel<IdTypes>}
                    labelOverride={`${toUppercase(customFieldConstants.entitySingular)}-ID: ${customField.id}`}
                    openInNewTab
                  />
                </span>
              </div>
            </div>
            {customField.id && (
              <button
                className="btn btn-sm btn-default"
                onClick={() =>
                  customField.id &&
                  history.push(route(getEditLink(customFieldConstants.frontendIndexRoute, customField.id)))
                }
                onMouseDown={(e) => e.stopPropagation()}
                title="Edit this custom field"
                disabled={!canEdit}
              >
                <LucideIcon name="square-pen" /> Edit
              </button>
            )}
            {removeCustomField && (
              <button
                className="btn btn-sm btn-soft-danger"
                onClick={() => removeCustomField(elementId)}
                onMouseDown={(e) => e.stopPropagation()}
                title="Remove this custom field"
              >
                <LucideIcon name="x" />
              </button>
            )}
          </div>
        </div>
      }
      ref={ref}
    >
      <div className="flex col-nowrap" style={{ width: "100%" }}>
        {/* <DetailsPageTopControlsView entityTypeId={entityTypeId} entity={customField} entityConstants={customFieldConstants} /> */}
        <CustomFieldsDetailTable
          entityTypeId="customFields"
          entity={customField as CustomField}
          nonFoldable
          entityConstants={customFieldConstants}
          fieldLabels={CustomFieldFieldLabels}
        />
      </div>
    </GridLayoutElementWrapper>
  );
};

interface LayoutStateSectionCustomField {
  [elementId: string]: {
    field: CustomField;
    layout: GridLayout.Layout;
  };
}

interface RenderCustomTypeSectionProps {
  elementId: string;
  section: CustomTypeSection;
  nodeHeightCallback: (uuid: string, height: number) => void;
  canEdit: boolean;
}
const RenderCustomTypeSection = ({ elementId, section, nodeHeightCallback, canEdit }: RenderCustomTypeSectionProps) => {
  const { ref } = useDynamicHeight({
    uuid: elementId,
    nodeHeightCallback: nodeHeightCallback,
    gridRowHeight: 1,
  });
  const { ref: internalRef, width } = useResizeDetector();
  const [layout, setLayout] = useState<LayoutStateSectionCustomField>(
    Object.fromEntries(
      Object.entries(section.customFields).map(([k, v]) => {
        const elementId = uuidv4();
        return [elementId, { field: v, layout: { i: elementId, x: 0, y: 0, w: 1, h: 6 } }];
      })
    )
  );

  useEffect(() => {
    setLayout((prev) =>
      Object.fromEntries(
        Object.entries(section.customFields).map(([k, v]) => {
          const elementId = uuidv4();
          return [
            elementId,
            { field: v, layout: { i: elementId, x: 0, y: 0, w: 1, h: prev?.[elementId]?.layout?.h || 6 } },
          ];
        })
      )
    );
  }, [section.customFields]);

  // This callback will dynamically adjust the layout based on the height of the widgets
  const childNodeHeightCallback = useCallback((elementId: string, height: number) => {
    // console.log("Component reported height: ", height, " with elementId: ", elementId);
    setLayout((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], layout: { ...prev[elementId].layout, h: height } },
    }));
  }, []);

  return (
    <GridLayoutElementWrapper
      head={
        <div className="flex row-nowrap align-center gap-5" style={{ width: "100%", height: "100%" }}>
          <LucideIcon name={"panel-top-dashed"} style={{ width: "24px", height: "24px" }} />

          {!!section.isHeadless ? (
            <div className="label label-soft-info">Section header hidden by settings</div>
          ) : (
            <>
              <h4 style={{ margin: 0 }}>{section.name}</h4>
              <div style={{ marginLeft: "auto" }}>
                Default folded: <ToggleIndicator enabled={!!section.isFolded} />
              </div>
            </>
          )}
        </div>
      }
      ref={ref}
    >
      <div className={styles.rglMainContainerSection} ref={internalRef}>
        {!!width && (
          <div style={{ flex: "1 0 100%", position: "relative" }}>
            <GridLayout
              className={styles.rglContainerSection}
              layout={Object.values(layout).map((l) => l.layout)}
              cols={1}
              rowHeight={1}
              width={width}
              verticalCompact={true}
              // margin={[0, 0]}
              containerPadding={[5, 7]}
              compactType={"vertical"}
              preventCollision={false}
              isDroppable={false}
              isDraggable={false}
              isResizable={false}
              isBounded={true}
              droppingItem={{ i: "new", w: 1, h: 7 }}
            >
              {Object.entries(layout).map(([elementId, l]) => (
                <div key={elementId} className="customField" data-grid={l.layout}>
                  <RenderCustomTypeSectionCustomField
                    elementId={elementId}
                    customField={l.field}
                    nodeHeightCallback={childNodeHeightCallback}
                    canEdit={canEdit}
                  />
                </div>
              ))}
            </GridLayout>
          </div>
        )}
      </div>
    </GridLayoutElementWrapper>
  );
};

interface LayoutStateSection {
  [elementId: string]: {
    field: CustomTypeSection;
    layout: GridLayout.Layout;
  };
}

interface CustomTypeDetailTableProps extends EntityDetailTableProps<"customTypes"> {
  hideConfigurationDetails?: boolean;
}

export const CustomTypeDetailTable = ({
  permissions,
  routes,
  entity,
  entityConstants,
  fieldLabels,
  hideConfigurationDetails,
}: CustomTypeDetailTableProps) => {
  const { route } = useContext(SessionContext);

  const { ref, width } = useResizeDetector();

  const [layout, setLayout] = useState<LayoutStateSection>(
    Object.fromEntries(
      Object.entries(entity.sections).map(([k, v]) => {
        const elementId = uuidv4();
        return [elementId, { field: v, layout: { i: elementId, x: 0, y: 0, w: 1, h: 6 } }];
      })
    )
  );
  useEffect(() => {
    setLayout((prev) =>
      Object.fromEntries(
        Object.entries(entity.sections).map(([k, v]) => {
          const elementId = uuidv4();
          return [
            elementId,
            { field: v, layout: { i: elementId, x: 0, y: 0, w: 1, h: prev?.[elementId]?.layout?.h || 6 } },
          ];
        })
      )
    );
  }, [entity.sections]);

  // This callback will dynamically adjust the layout based on the height of the widgets
  const nodeHeightCallback = useCallback((elementId: string, height: number) => {
    // console.log("Component reported height: ", height, " with elementId: ", elementId);
    setLayout((prev) => ({
      ...prev,
      [elementId]: { ...prev[elementId], layout: { ...prev[elementId].layout, h: height } },
    }));
  }, []);

  return (
    <>
      <Table noPadding>
        {entity.inventoryName && (
          <>
            <Table.Body.RowContent
              title={fieldLabels.inventoryName}
              content={
                <div className={"container_label"}>
                  <div className={"container_label_name"} style={{ fontWeight: 600 }}>
                    {entity.inventoryName ?? NotSet}
                  </div>
                </div>
              }
            />
            <Table.Body.RowContent
              title={fieldLabels.inventoryDescription}
              content={
                <>
                  {entity.inventoryDescription ? (
                    <div className={"container_notes"}>
                      <textarea
                        rows={4}
                        className={"container_textarea"}
                        value={entity.inventoryDescription}
                        disabled
                      />
                    </div>
                  ) : (
                    NotSet
                  )}
                </>
              }
            />
            {!hideConfigurationDetails && (
              <Table.Body.RowContent
                title={fieldLabels.isHierarchyRoot}
                content={<ToggleIndicator enabled={!!entity.isHierarchyRoot} />}
              />
            )}
          </>
        )}

        {!hideConfigurationDetails && (
          <Table.Body.RowContent
            title={
              entity.entityType && CustomTypeDataTypeUtils.CanDefineHierarchy(entity.entityType)
                ? `${toUppercase(hierarchyConstants.childType)} name`
                : fieldLabels.name
            }
            content={
              <div className={"container_label"}>
                <div className={"container_label_name"} style={{ fontWeight: 600 }}>
                  {entity.name ?? NotSet}
                </div>
                <div className={"container_label_id"}>
                  <span style={{ whiteSpace: "nowrap" }}>
                    {toUppercase(entityConstants.entitySingular)}-ID: {entity.id ?? NotAvailable}
                  </span>
                </div>
              </div>
            }
          />
        )}
        {!hideConfigurationDetails && (
          <Table.Body.RowContent
            title={fieldLabels.entityType}
            content={
              <div className="flex row-nowrap align-center gap-5">
                <span>
                  <CustomTypeEntityTypeOptionToIcon entityType={entity.entityType} color={"var(--primary)"} />{" "}
                  {entity.entityType ? camelCaseToWords(entity.entityType) : NotSet}
                </span>
              </div>
            }
          />
        )}
        {!hideConfigurationDetails && !!entity.customTypeTemplate && (
          <Table.Body.RowContent
            title={fieldLabels.customTypeTemplate}
            content={
              <LinkEntity
                entityConstants={customTypeTemplateConstants}
                property={entity.customTypeTemplate}
                showLink={true}
                routeOverride={
                  route(getIndexRoute(customTypeTemplateConstants.frontendIndexRoute)) +
                  `?entity_type=Dataset&step=2&template_id=${entity.customTypeTemplate.id}`
                }
                labelOverride={
                  entity.customTypeTemplate.name +
                  (entity.templateVersion ? ` (Version: ${entity.templateVersion})` : "")
                }
              />
            }
          />
        )}

        {!hideConfigurationDetails && !!entity.integrations && (
          <Table.Body.RowContent
            title={fieldLabels.integrations}
            content={
              <TableArrayRenderer values={entity.integrations} arrayLength={5}>
                {(e) => <span className="label label-soft-default">{e}</span>}
              </TableArrayRenderer>
            }
          />
        )}
        {CustomTypeDataTypeUtils.CanDefineHierarchy(entity.entityType) && entity.rootHierarchy && (
          <>
            <Table.Body.RowContent
              title={fieldLabels.rootHierarchy}
              content={
                <>
                  {entity.rootHierarchy ? (
                    <LinkEntity entityConstants={entityConstants} property={entity.rootHierarchy} />
                  ) : (
                    NotSet
                  )}
                </>
              }
            />
            {/* <Table.Body.RowContent
              title={fieldLabels.parentTypes}
              content={
                <>
                  {Array.isArray(customType.parentTypes) ? (
                    <RenderMinimalEntity
                      entities={customType.parentTypes}
                      entityConstants={entityConstants}
                      createLinks
                      separator=",&nbsp;"
                    />
                  ) : (
                    NotSet
                  )}
                </>
              }
            /> */}
          </>
        )}
        <Table.Body.RowContent
          title={fieldLabels.description}
          content={
            <>
              {entity.description ? (
                <div className={"container_notes"}>
                  <textarea rows={4} className={"container_textarea"} value={entity.description} disabled />
                </div>
              ) : (
                NotSet
              )}
            </>
          }
        />
        {!hideConfigurationDetails &&
          entity.entityType &&
          CustomTypeDataTypeUtils.CanDefineHierarchy(entity.entityType) && (
            <>
              <Table.Body.RowContent
                title={fieldLabels.hasRestrictedAddPermission}
                content={<ToggleIndicator enabled={!!entity.hasRestrictedAddPermission} />}
              />
              <Table.Body.RowContent
                title={fieldLabels.hasRestrictedEditPermission}
                content={<ToggleIndicator enabled={!!entity.hasRestrictedEditPermission} />}
              />
              <Table.Body.RowContent
                title={fieldLabels.hasRestrictedReadPermission}
                content={<ToggleIndicator enabled={!!entity.hasRestrictedReadPermission} />}
              />
            </>
          )}

        {!hideConfigurationDetails && (
          <Table.Body.RowContent
            title={fieldLabels.isEnabled}
            content={<ToggleIndicator enabled={entity.isEnabled} />}
          />
        )}
      </Table>
      {!hideConfigurationDetails && (
        <>
          <h2>Sections</h2>
          <h3>Default sections</h3>
          <RenderCustomTypeDefaultSections entityType={entity.entityType} />
          <h3>Custom sections</h3>
          {Array.isArray(entity.sections) && !!entity.sections.length ? (
            <div className="flex col-nowrap">
              <div className={`${styles.rglMainContainer}`} style={{ minHeight: "min-content" }} ref={ref}>
                <GridLayout
                  className={`${styles.rglContainer} `}
                  layout={Object.values(layout).map((l) => l.layout)}
                  cols={1}
                  rowHeight={1}
                  width={width}
                  verticalCompact={true}
                  margin={[10, 10]}
                  containerPadding={[10, 10]}
                  compactType={"vertical"}
                  preventCollision={false}
                  isDroppable={false}
                  isResizable={false}
                  isDraggable={false}
                >
                  {Object.entries(layout).map(([elementId, l]) => (
                    <div key={elementId} className="section" data-grid={l.layout}>
                      <RenderCustomTypeSection
                        elementId={elementId}
                        section={l.field}
                        nodeHeightCallback={nodeHeightCallback}
                        canEdit={permissions ? permissions.canEdit(entity) : false}
                      />
                    </div>
                  ))}
                </GridLayout>
              </div>
            </div>
          ) : (
            <Alert type="info" message={"No custom sections defined"} fit centered />
          )}
        </>
      )}
      <CustomTypeHierarchyViewer customType={entity} showAddNode={!hideConfigurationDetails} />
    </>
  );
};

export const CustomTypeDetail = ({
  permissions,
  routes,
  entity,
  entityApi,
  entityConstants,
  fieldLabels,
  entityTypeId,
}: EntityDetailProps<"customTypes">) => {
  return (
    <Detail
      head={
        <DetailsPageTopControlsView
          entityTypeId={entityTypeId}
          entity={entity}
          entityConstants={entityConstants}
          fullTitleOverride={<RenderCustomTypeHierarchy customType={entity} />}
          controls={
            <>
              {(!!entity.rootHierarchy?.id || entity.isHierarchyRoot) && (
                <LinkButton
                  className="btn btn-primary"
                  linkTo={`${routes.getAddRoute}?parentTypes=${JSON.stringify([
                    {
                      id: entity.id,
                      name: entity.name,
                    },
                  ])}&rootHierarchy=${JSON.stringify(
                    entity.rootHierarchy
                      ? {
                          id: entity.rootHierarchy.id,
                          name: entity.rootHierarchy.name,
                        }
                      : {
                          id: entity.id,
                          name: entity.name,
                        }
                  )}&entityType=InventoryItem`}
                  disabled={!permissions.canCreate}
                >
                  <LucideIcon name={hierarchyConstants.childIcon} />
                  <span> Add child {hierarchyConstants.childType}</span>
                </LinkButton>
              )}
              <EntityEditButton
                entity={entity}
                entityConstants={entityConstants}
                permissions={permissions}
                routes={routes}
              />
              <CustomTypePreviewButton customType={entity} />
              <MoreDropdown drop="right" btn="btn btn-ghost-secondary">
                <li>
                  <EntityCloneButton
                    entity={entity}
                    entityConstants={entityConstants}
                    routes={routes}
                    permissions={permissions}
                  />
                </li>
                <li className="divider" />
                <li>
                  <EntityDeleteButton entity={entity} entityTypeId={entityTypeId} alwaysDeletePermanently />
                </li>
              </MoreDropdown>
            </>
          }
        />
      }
      mainStyle={{ padding: "0px 10px" }}
      main={
        <CustomTypeDetailTable
          entity={entity}
          entityConstants={entityConstants}
          fieldLabels={fieldLabels}
          entityTypeId={entityTypeId}
          permissions={permissions}
        />
      }
      sidebar={
        <EntitySidebarTileAutosorter>
          {/* TODO */}
          {entity.entityType === "Dataset" && (
            <DatasetsTile
              defaultFilters={{ customTypeIds: [entity.id] }}
              excludeFilters={{ customTypeIds: () => false }}
            />
          )}
          {entity.entityType === "Sample" && (
            <SamplesTile
              defaultFilters={{ customTypeIds: [entity.id] }}
              excludeFilters={{ customTypeIds: () => false }}
            />
          )}
          {entity.entityType === "InventoryItem" && (
            <InventoriesTile
              defaultFilters={{ customTypeIds: [entity.id] }}
              excludeFilters={{ customTypeIds: () => false }}
            />
          )}
          {entity.entityType === "Project" && (
            <ProjectsTile
              defaultFilters={{ customTypeIds: [entity.id] }}
              excludeFilters={{ customTypeIds: () => false }}
            />
          )}
          {entity.entityType === "Person" && (
            <PersonsTile
              defaultFilters={{ customTypeIds: [entity.id] }}
              excludeFilters={{ customTypeIds: () => false }}
            />
          )}
        </EntitySidebarTileAutosorter>
      }
    />
  );
};
